import { keycloak, updateTokenOrTimeout } from './keycloak';

/**
 * Minimal REST client for the gateway's non-GraphQL surface (ENT-88 `/auth/*`).
 *
 * The repo otherwise speaks only GraphQL via {@link ./graphql_client}. The
 * session/context bootstrap endpoints are REST, so this helper mirrors the
 * GraphQL client's Keycloak token-injection exactly:
 *   - refresh the token if it expires within 30s, then attach it as a Bearer
 *   - never set `x-gateway-secret` — the gateway injects trusted headers
 *     downstream (handover §4).
 *
 * Base URL resolves to the gateway origin (NOT the `/graphql` path):
 *   VITE_GATEWAY_URL ?? VITE_GRAPHQL_URL minus `/graphql` ?? http://localhost:4000
 */
const GATEWAY_URL =
  (import.meta.env.VITE_GATEWAY_URL as string | undefined) ??
  (import.meta.env.VITE_GRAPHQL_URL as string | undefined)?.replace(/\/graphql\/?$/, '') ??
  'http://localhost:4000';

/** Thrown for any non-2xx REST response; `status` is preserved so callers can branch (e.g. 403). */
export class AuthHttpError extends Error {
  readonly status: number;
  readonly code?: string;

  // Explicit field assignment (not constructor parameter properties) — the
  // build's tsconfig enables `erasableSyntaxOnly`, which forbids them.
  constructor(status: number, message: string, code?: string) {
    super(message);
    this.name = 'AuthHttpError';
    this.status = status;
    this.code = code;
  }
}

export interface AuthFetchInit extends Omit<RequestInit, 'body'> {
  /** Serialised to JSON automatically; omit for bodyless requests (GET / logout). */
  body?: unknown;
}

/**
 * Fetch a gateway REST endpoint with the Keycloak bearer token attached.
 *
 * @returns parsed JSON as `T`, or `undefined` for `204 No Content`.
 * @throws {AuthHttpError} on any non-2xx response.
 */
export async function authFetch<T = unknown>(path: string, init: AuthFetchInit = {}): Promise<T> {
  // Mirror graphql_client: refresh if near expiry. Swallow failures — the
  // keycloak auto-refresh interval / login-required init handles re-auth.
  // EW-FIX-95 — `updateTokenOrTimeout`, not the bare call: a hung refresh (never
  // resolves, never rejects — see keycloak.ts) would otherwise stall this fetch
  // forever instead of swallowing a real failure and proceeding.
  await updateTokenOrTimeout(30).catch(() => {
    /* token refresh failed or timed out; keycloak middleware will re-login */
  });

  const { body, headers, ...rest } = init;

  const res = await fetch(`${GATEWAY_URL}${path}`, {
    ...rest,
    headers: {
      'content-type': 'application/json',
      ...headers,
      authorization: `Bearer ${keycloak.token}`,
    },
    body: body == null ? undefined : JSON.stringify(body),
  });

  if (res.status === 204) return undefined as T;

  let data: unknown;
  try {
    data = await res.json();
  } catch {
    // A 2xx response that isn't valid JSON means the request never reached
    // the gateway (e.g. a misconfigured GATEWAY_URL resolving to a relative
    // path, silently served the SPA's index.html by the host's fallback
    // rewrite). Swallowing this as `null` here previously surfaced as
    // unrelated downstream errors (e.g. "Could not determine the
    // administrator") with no trace of the real cause.
    if (res.ok) {
      throw new AuthHttpError(
        res.status,
        `Expected a JSON response from ${path} but the body was not parseable JSON (content-type: ${res.headers.get('content-type') ?? 'unknown'}). Check GATEWAY_URL configuration.`,
      );
    }
    data = null;
  }

  if (!res.ok) {
    const message =
      (data && typeof data === 'object' && 'message' in data && typeof data.message === 'string'
        ? data.message
        : undefined) ?? `Request failed (${res.status})`;
    const code =
      data && typeof data === 'object' && 'code' in data && typeof data.code === 'string'
        ? data.code
        : undefined;
    throw new AuthHttpError(res.status, message, code);
  }

  return data as T;
}
